import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    Bundle,
    FeatureState,
    Release,
    SiteCredentials,
    SiteFeature,
    UserToken,
} from '@myrtls/api-interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    filter,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    BUNDLES_ENDPOINT,
    CREATE_TEMPLATES_ENDPOINT,
    CREDENTIALS_ENDPOINT,
    DEPLOYMENTS_ENDPOINT,
    DEVICE_CARE_ENDPOINT,
    PERMISSIONS_API_URL,
    RELEASES_BETA_ENDPOINT,
    RELEASES_ENDPOINT,
    SITES_API_URL,
    VERSION_API_URL,
} from '../../shared/utils/constants';
import { AppState } from '../index';
import { showSnackBarAction } from '../snack-bar/snack-bar.actions';
import { $selectedSiteId, $siteFeature } from '../sites/sites.selectors';
import { SnackBarType } from '../../shared/models/snack-bar-config.model';
import { interval, of } from 'rxjs';
import { ActivationStart, Router } from '@angular/router';
import {
    createSiteDeploymentBundleAction,
    createSiteDeploymentBundleSuccessAction,
    createSiteTemplatesFailureAction,
    createSiteTemplatesRequestAction,
    createSiteTemplatesSuccessAction,
    deploySiteFailureAction,
    deploySiteRequestAction,
    deploySiteSuccessAction,
    downloadSiteDeploymentAction,
    downloadSiteDeploymentFailureAction,
    downloadSiteDeploymentSuccessAction,
    getCredentialsFailureAction,
    getCredentialsRequestAction,
    getCredentialsSuccessAction,
    getSiteDeploymentBundlesAction,
    getSiteDeploymentBundlesFailureAction,
    getSiteDeploymentBundlesSuccessAction,
    getSiteReleasesAction,
    getSiteReleasesFailureAction,
    getSiteReleasesSuccessAction,
    getVersionAction,
    getVersionFailureAction,
    getVersionSuccessAction,
    startPoolingSiteDeploymentBundlesAction,
    stopPoolingSiteDeploymentBundlesAction,
} from './deployment.actions';
import {
    getSiteFeaturesRequestAction,
    getSiteRequestAction,
} from '../sites/sites.actions';
import { RoutePath } from '../../shared/models/route-path.enum';

@Injectable()
export class DeploymentEffects {
    private httpWithoutAuth: HttpClient;

    deploySite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deploySiteRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([request, siteId]) =>
                this.http
                    .put<void>(
                        `${SITES_API_URL}/${siteId}/${DEPLOYMENTS_ENDPOINT}`,
                        { deploymentType: request.deploymentType },
                    )
                    .pipe(
                        mergeMap(() => [
                            getSiteRequestAction(),
                            getSiteFeaturesRequestAction(),
                            deploySiteSuccessAction(),
                        ]),
                        catchError(error =>
                            of(
                                deploySiteFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    startPoolingDeploymentBundles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startPoolingSiteDeploymentBundlesAction),
            mergeMap(({ intervalTime, bundleType }) =>
                interval(intervalTime).pipe(
                    takeUntil(
                        this.actions$.pipe(
                            ofType(stopPoolingSiteDeploymentBundlesAction),
                        ),
                    ),
                    map(() =>
                        getSiteDeploymentBundlesAction({
                            bundleType,
                        }),
                    ),
                ),
            ),
        ),
    );

    getReleases$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteReleasesAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
                this.store.pipe(
                    select($siteFeature(SiteFeature.DEPLOYMENT_STUDIO_BETA)),
                ),
            ),
            switchMap(([, siteId, betaFeature]) => {
                return this.http
                    .get<
                        Release[]
                    >(`${SITES_API_URL}/${siteId}/${DEPLOYMENTS_ENDPOINT}/${betaFeature?.state === FeatureState.ENABLED ? RELEASES_BETA_ENDPOINT : RELEASES_ENDPOINT}`)
                    .pipe(
                        map(releases =>
                            getSiteReleasesSuccessAction({
                                releases,
                            }),
                        ),
                        catchError(error =>
                            of(
                                getSiteReleasesFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    getDeploymentBundles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSiteDeploymentBundlesAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            mergeMap(([{ bundleType }, siteId]) => {
                return this.http
                    .get<
                        Bundle[]
                    >(`${SITES_API_URL}/${siteId}/${DEPLOYMENTS_ENDPOINT}/${BUNDLES_ENDPOINT}/${bundleType}`)
                    .pipe(
                        map(response =>
                            getSiteDeploymentBundlesSuccessAction({
                                bundles: response,
                                bundleType,
                            }),
                        ),
                        catchError(error =>
                            of(
                                getSiteDeploymentBundlesFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    createDeploymentBundle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createSiteDeploymentBundleAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ bundleType, version }, siteId]) =>
                this.http
                    .post<Bundle>(
                        `${SITES_API_URL}/${siteId}/${DEPLOYMENTS_ENDPOINT}/${BUNDLES_ENDPOINT}/${bundleType}`,
                        {
                            version,
                        },
                    )
                    .pipe(
                        switchMap(response => [
                            createSiteDeploymentBundleSuccessAction({
                                bundle: response,
                            }),
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    description:
                                        'Bundle generation has been started. It may take a few minutes. You will be notified by email once the bundle is generated.',
                                    actionLabel: 'Dismiss',
                                },
                            }),
                        ]),
                        catchError(error =>
                            of(
                                getSiteDeploymentBundlesFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    downloadDeployment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadSiteDeploymentAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ uuid, bundleType }, siteId]) =>
                this.http.get<UserToken>(`${PERMISSIONS_API_URL}/token`).pipe(
                    map(response => {
                        const url = this.router.serializeUrl(
                            this.router.createUrlTree([
                                `${SITES_API_URL}/${siteId}/${DEPLOYMENTS_ENDPOINT}/${BUNDLES_ENDPOINT}/${bundleType}/${uuid}`,
                            ]),
                        );

                        window.open(
                            url +
                                '?token=' +
                                encodeURIComponent(response.token),
                            '_blank',
                        );

                        return downloadSiteDeploymentSuccessAction();
                    }),
                    catchError(error =>
                        of(downloadSiteDeploymentFailureAction({ error })),
                    ),
                ),
            ),
        ),
    );

    getVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getVersionAction),
            withLatestFrom(
                this.router.events.pipe(
                    filter(
                        (event): event is ActivationStart =>
                            event instanceof ActivationStart,
                    ),
                ),
            ),
            switchMap(([, event]) => {
                const params = event.snapshot.queryParams;
                let httpVersion;

                if (params.serverToken) {
                    httpVersion = this.httpWithoutAuth.get<{ version: string }>(
                        VERSION_API_URL,
                        {
                            headers: {
                                Authorization: 'Bearer ' + params.serverToken,
                            },
                        },
                    );
                } else {
                    httpVersion = this.http.get<{ version: string }>(
                        VERSION_API_URL,
                    );
                }

                return httpVersion.pipe(
                    map(response => getVersionSuccessAction(response)),
                    catchError(error =>
                        of(
                            getVersionFailureAction({
                                error,
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    createDashboardTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createSiteTemplatesRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .put<void>(
                        `${SITES_API_URL}/${siteId}/${DEVICE_CARE_ENDPOINT}/${CREATE_TEMPLATES_ENDPOINT}`,
                        {},
                    )
                    .pipe(
                        mergeMap(() => [createSiteTemplatesSuccessAction()]),
                        catchError(error =>
                            of(
                                createSiteTemplatesFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    onDashboardTemplatesCreated$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(createSiteTemplatesSuccessAction),
                withLatestFrom(
                    this.store.pipe(
                        select($selectedSiteId),
                        filter((id): id is string => id != null),
                    ),
                ),
                tap(([, siteId]) => {
                    console.log('Navigating to Advanced Analytics...');
                    this.router.navigateByUrl(
                        `/${RoutePath.SITE}/${siteId}/${SiteFeature.DEVICE_CARE_GRAFANA}`,
                    );
                }),
            ),
        { dispatch: false },
    );

    getCredentials$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCredentialsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<SiteCredentials>(
                        `${SITES_API_URL}/${siteId}/${CREDENTIALS_ENDPOINT}`,
                    )
                    .pipe(
                        map(credentials =>
                            getCredentialsSuccessAction({ credentials }),
                        ),
                        catchError(error =>
                            of(getCredentialsFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store<AppState>,
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly httpBackend: HttpBackend,
    ) {
        this.httpWithoutAuth = new HttpClient(this.httpBackend);
    }
}
